import React, { useMemo } from 'react';
import { TaskTemplate } from '@models/taskTemplate.model';
import { formatForDisplay } from '@utils/formatDate';
import { cn } from '@utils/cn';
import {
  Layers,
  Calendar,
  Trash2,
  Package,
  Wrench,
  Clock,
  DollarSign,
  ChevronRight,
} from 'lucide-react';

interface TaskTemplateCardProps {
  template: TaskTemplate;
  isSelected?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
}

export const TaskTemplateCard: React.FC<TaskTemplateCardProps> = ({
  template,
  isSelected = false,
  onClick,
  onDelete,
}) => {
  const taskCount = template.tasks?.length || 0;
  const parentTasks = useMemo(
    () => template.tasks?.filter((t) => !t.parentTempId) || [],
    [template.tasks]
  );
  const parentTaskCount = parentTasks.length;
  const subtaskCount = taskCount - parentTaskCount;

  const { materialsCount, equipmentCount, totalManHours, totalBudget } =
    useMemo(() => {
      let materials = 0;
      let equipment = 0;
      let manHours = 0;
      let budget = 0;

      template.tasks?.forEach((task) => {
        materials += task.materials?.length || 0;
        equipment += task.equipments?.length || 0;
        manHours += task.manHours || 0;
        budget += task.budget || 0;
      });

      return {
        materialsCount: materials,
        equipmentCount: equipment,
        totalManHours: manHours,
        totalBudget: budget,
      };
    }, [template.tasks]);

  // Determine template size category for accent color
  const sizeCategory = useMemo(() => {
    if (taskCount <= 5) return 'small';
    if (taskCount <= 15) return 'medium';
    return 'large';
  }, [taskCount]);

  const accentColors = {
    small: 'bg-emerald-500',
    medium: 'bg-blue-500',
    large: 'bg-purple-500',
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  // Show first 3 parent task names as preview
  const previewTasks = parentTasks.slice(0, 3);
  const remainingTasks = parentTaskCount - previewTasks.length;

  return (
    <div
      onClick={onClick}
      className={cn(
        'relative rounded-lg border-2 cursor-pointer transition-all overflow-hidden group',
        isSelected
          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md'
      )}>
      {/* Accent bar */}
      <div className={cn('h-1 w-full', accentColors[sizeCategory])} />

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {template.name}
            </h3>
            {template.description && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                {template.description}
              </p>
            )}
          </div>
          {onDelete && (
            <button type="button"
              onClick={handleDelete}
              className="ml-2 p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors opacity-0 group-hover:opacity-100"
              title="Delete template">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Stats badges */}
        <div className="mt-3 flex flex-wrap gap-2">
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300">
            <Layers className="w-3 h-3" />
            <span>
              {parentTaskCount} task{parentTaskCount !== 1 ? 's' : ''}
              {subtaskCount > 0 && (
                <span className="text-gray-400 dark:text-gray-500">
                  {' '}
                  +{subtaskCount} sub
                </span>
              )}
            </span>
          </div>
          {totalManHours > 0 && (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 dark:bg-blue-900/30 text-xs text-blue-700 dark:text-blue-300">
              <Clock className="w-3 h-3" />
              <span>{totalManHours}h</span>
            </div>
          )}
          {totalBudget > 0 && (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-50 dark:bg-green-900/30 text-xs text-green-700 dark:text-green-300">
              <DollarSign className="w-3 h-3" />
              <span>${totalBudget.toLocaleString()}</span>
            </div>
          )}
          {materialsCount > 0 && (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-orange-50 dark:bg-orange-900/30 text-xs text-orange-700 dark:text-orange-300">
              <Package className="w-3 h-3" />
              <span>{materialsCount}</span>
            </div>
          )}
          {equipmentCount > 0 && (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-50 dark:bg-purple-900/30 text-xs text-purple-700 dark:text-purple-300">
              <Wrench className="w-3 h-3" />
              <span>{equipmentCount}</span>
            </div>
          )}
        </div>

        {/* Task preview */}
        {previewTasks.length > 0 && (
          <div className="mt-3 space-y-1">
            {previewTasks.map((task) => (
              <div
                key={task.tempId}
                className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <div className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
                <span className="truncate">{task.name}</span>
              </div>
            ))}
            {remainingTasks > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 pl-3">
                <span>
                  +{remainingTasks} more task{remainingTasks !== 1 ? 's' : ''}
                </span>
                <ChevronRight className="w-3 h-3" />
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        {template.createdAt && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
            <Calendar className="w-3 h-3" />
            <span>{formatForDisplay(template.createdAt)}</span>
          </div>
        )}
      </div>

      {isSelected && (
        <div className="absolute top-3 right-2 w-2 h-2 bg-indigo-500 rounded-full" />
      )}
    </div>
  );
};

export default TaskTemplateCard;
